import React, { useEffect } from "react";
import headerAbout from "./assets/vitrine/about/header_about.jpg";
import "./AboutPage.css";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";

const TECHNOLOGY_CARDS = [
  {
    title: "The Loom Engine",
    text: "A distributed cloud stitching architecture that ingests, synchronizes, and sequences raw footage from multiple distributed mobile contributors into seamless, continuous media streams in real time.",
  },
  {
    title: "Dual AI & AI Scene Wizard",
    text: "An end-to-end intelligent pipeline pairing on-device filming guidance with automated cloud-based audio balancing, color normalization, and transition rhythm optimization.",
  },
  {
    title: "The 6 Collaborative Frameworks",
    text: "Standardized production blueprints (Versus, Gallery, Relay, Seed, Storyboard, Symphony) that eliminate technical filmmaking barriers and enforce visual coherence across disparate community contributors.",
  },
];

const PRINCIPLE_CARDS = [
  {
    title: "100% Contributor Attribution",
    text: "Every Weaver retains verifiable ownership and explicit on-screen credit for every second of footage contributed to a Living Weave.",
  },
  {
    title: "Fair Automated Monetization",
    text: "Powered by our In-App Wallet & Escrow protocol, incoming revenue from commercial licenses, municipal sponsorships, and tips is split instantly and transparently among verified contributors.",
  },
  {
    title: "Decentralized Authenticity",
    text: "Replacing centralized, one-sided promotional marketing with an authentic, choral tapestry curated by local historians, guides, artisans, and residents.",
  },
  {
    title: "Enterprise Security & Data Sovereignty",
    text: "Built with strict GDPR compliance, encrypted media storage, and enterprise-grade cloud infrastructure engineered to meet institutional and municipal standards.",
  },
];

const MARKET_CARDS = [
  {
    title: "Tourism & Smart Cities",
    text: "Perpetual walking tours, municipal heritage archives, and decentralized destination marketing.",
  },
  {
    title: "Cultural Heritage & Museums",
    text: "Multi-perspective curatorial guides, living exhibition walk-throughs, and evolving historical records.",
  },
  {
    title: "Live Events & Festivals",
    text: "Synchronized choral narratives capturing festival stages, crowd energy, and backstage access from multiple simultaneous viewpoints.",
  },
  {
    title: "Real Estate & Urban Development",
    text: "Dynamic construction progress tracking, modular architectural portfolios, and evolving neighborhood showcases.",
  },
];

function ContentCard({ title, text }) {
  return (
    <article className="about-card">
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  );
}

export default function AboutPage() {
  useEffect(() => {
    document.title = "About Weavevo | AI-Powered Collaborative Media";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="about-page">
      <section className="about-hero">
        <img className="about-heroImage" src={headerAbout} alt="" />
        <div className="about-heroScrim" />
        <div className="about-heroContent">
          <span className="about-eyebrow">OUR MISSION</span>
          <h1>Pioneering the Era of Collaborative Living Media</h1>
          <p>Replacing static, disposable video formats with modular, perpetually updated media assets co-created by decentralized communities.</p>
        </div>
      </section>

      <section className="about-section about-genesis">
        <div className="about-sectionHeading">
          <span>Genesis — The Paradigm Shift</span>
          <h2>The Static Video Dilemma</h2>
        </div>
        <div className="about-twoColumns">
          <article>
            <h3>The Broken Standard</h3>
            <p>The physical world evolves continuously: cities transform, businesses relocate, and cultural landmarks change. Yet, conventional online video remains frozen the moment it is exported. A single outdated detail forces creators to delete or re-upload entire videos—breaking syndication links, invalidating printed QR codes, and wiping out accumulated audience metrics.</p>
          </article>
          <article>
            <h3>The Living Alternative</h3>
            <p>Weavevo shifts video from an immutable, disposable file into an agile, modular assembly of geolocated scenes. Powered by real-time cloud stitching, individual capsules can be hot-swapped and refreshed indefinitely by field contributors without ever breaking the distribution endpoint.</p>
          </article>
        </div>
      </section>

      <section className="about-section about-sectionSoft">
        <div className="about-sectionHeading">
          <span>The Proprietary Technology Core</span>
          <h2>Architected for Perpetual Assembly</h2>
        </div>
        <div className="about-cardGrid about-cardGridThree">
          {TECHNOLOGY_CARDS.map((item) => <ContentCard key={item.title} {...item} />)}
        </div>
      </section>

      <section className="about-section">
        <div className="about-sectionHeading">
          <span>Core Values & Operating Principles</span>
          <h2>Built on Fair Principles</h2>
        </div>
        <div className="about-cardGrid">
          {PRINCIPLE_CARDS.map((item) => <ContentCard key={item.title} {...item} />)}
        </div>
      </section>

      <section className="about-section about-sectionSoft">
        <div className="about-sectionHeading">
          <span>Target Ecosystems & Markets</span>
          <h2>Where Living Media Powers the Narrative</h2>
        </div>
        <div className="about-cardGrid">
          {MARKET_CARDS.map((item) => <ContentCard key={item.title} {...item} />)}
        </div>
      </section>

      <section className="about-section about-corporate">
        <div className="about-sectionHeading">
          <span>Corporate Foundation & Leadership</span>
          <h2>Leadership & Corporate Foundation</h2>
        </div>
        <div className="about-corporatePanel">
          <p>Founded by entrepreneurs with proven leadership across banking payment infrastructure, telecommunications network operations, and distributed cloud computing, Weavevo bridges enterprise-grade reliability with collaborative storytelling.</p>
          <strong>Weavevo is engineered and operated by WeaveTech SAS — Registered in France.</strong>
          <a className="about-leadershipLink" href="/team">Meet the Leadership Team →</a>
        </div>
      </section>

      <section className="about-finalCta">
        <h2>Deploy Living Media Across Your Organization</h2>
        <p>Join our exclusive enterprise pilot program to eliminate video obsolescence, automate collaborative storytelling, and engage your territory with perpetual media assets..</p>
        <div className="about-storeActions" aria-label="Download Weavevo">
          <a className="about-storeButton about-storeButtonPrimary" href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer">
            <i className="bi bi-google-play" aria-hidden="true" />
            <span>Play Store</span>
          </a>
          <button className="about-storeButton about-storeButtonSecondary" type="button" disabled>
            <i className="bi bi-apple" aria-hidden="true" />
            <span>App Store</span>
          </button>
        </div>
      </section>
    </main>
  );
}
